import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import WorkspacesOutlinedIcon from '@mui/icons-material/WorkspacesOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { useStore, useViewport } from '@xyflow/react';
import { createRafResizeObserver } from '../../Utils/resizeObserver';
import { getProjectDagSourcesEnd, getProjectDagActionsStart } from './projectDagSectionsLayout';

const selectWidth = (state) => state.width;
const selectCenterX = (state) => {
  const center = state.nodeLookup.get('center');
  return center?.internals?.positionAbsolute?.x ?? center?.position?.x;
};

function SectionHeading({ section, width }) {
  const titleRef = useRef(null);
  const [titleWidth, setTitleWidth] = useState(null);
  useLayoutEffect(() => {
    const title = titleRef.current;
    const measure = () => setTitleWidth(title.getBoundingClientRect().width);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = createRafResizeObserver(measure);
    observer.observe(title);
    return () => observer.disconnect();
  }, [section.title]);

  // Measure the unwrapped, translated title even while it is hidden. Only
  // heading geometry is read; no graph layout or saved position is changed.
  const compact = titleWidth === null || width < titleWidth + 28 + (section.onAdd ? 44 : 0) + 24;
  const Icon = section.icon;
  return <Box data-testid={`dag-section-heading-${section.id}`} data-compact={compact} sx={{
    position: 'absolute', top: 12, left: compact ? 8 : 12, right: compact ? 8 : 12, zIndex: 5,
    textAlign: 'center', color: section.color,
  }}>
    <Box data-testid={`dag-section-label-${section.id}`} sx={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: compact ? 1.5 : 1, minHeight: 40,
      flexDirection: compact && width < 112 ? 'column' : 'row',
      maxWidth: '100%', boxSizing: 'border-box', px: 1, py: 0.5,
      backgroundColor: 'rgba(251, 251, 255, 0.78)',
      border: '1px solid rgba(87, 87, 176, 0.16)', borderRadius: 2,
      boxShadow: '0 2px 6px rgba(35, 40, 75, 0.06)', pointerEvents: 'none',
    }}>
      <Tooltip title={compact ? section.title : section.subtitle} arrow>
        <Box component="span" role={compact ? 'img' : undefined} aria-label={compact ? section.title : undefined}
          tabIndex={compact ? 0 : undefined} sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            flexShrink: 0, pointerEvents: 'auto', borderRadius: 1,
            '&:focus-visible': { outline: '2px solid currentColor', outlineOffset: 2 },
          }}>
          <Icon sx={{ fontSize: 20, flexShrink: 0 }} />
          <Typography ref={titleRef} component="h3" aria-hidden={compact} sx={{
            m: 0, width: 'max-content', fontSize: 13, fontWeight: 650, lineHeight: 1.5,
            whiteSpace: 'nowrap', position: compact ? 'absolute' : 'static',
            visibility: compact ? 'hidden' : 'visible',
          }}>{section.title}</Typography>
        </Box>
      </Tooltip>
      {section.onAdd && <Tooltip title={section.addLabel} arrow>
        <IconButton className="nodrag nopan nowheel" size="small" aria-label={section.addLabel}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            section.onAdd();
          }}
          sx={{
            width: 36, height: 36, flexShrink: 0, pointerEvents: 'auto',
            color: section.color, backgroundColor: '#fff', border: '1px solid currentColor',
            '&:hover': { backgroundColor: '#f5f5ff' },
            '&.Mui-focusVisible': { outline: '2px solid currentColor', outlineOffset: 2 },
          }}>
          <AddIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>}
    </Box>
  </Box>;
}

// These are screen-space decorations, so labels remain readable while the
// boundaries follow the center node in graph coordinates. No extra DAG nodes.
export default function ProjectDagSections({ centerNodeWidth, t, onAddConnector, onAddFlow }) {
  const width = useStore(selectWidth);
  const centerX = useStore(selectCenterX);
  const { x, zoom } = useViewport();
  if (!width || !Number.isFinite(centerX)) return null;

  const leftBoundary = x + getProjectDagSourcesEnd(centerX) * zoom;
  const rightBoundary = x + getProjectDagActionsStart(centerX, centerNodeWidth) * zoom;
  const clamp = (value) => Math.max(0, Math.min(width, value));
  const boundaries = [0, clamp(leftBoundary), clamp(rightBoundary), width];
  const sections = [
    { id: 'sources', title: t('dag.section_sources', 'Monitor your data'), subtitle: t('dag.section_sources_hint', 'Follow new data as it arrives'),
      icon: MonitorHeartOutlinedIcon, color: '#346c83', background: 'rgba(52, 108, 131, 0.035)',
      onAdd: onAddConnector, addLabel: t('dag.add_connector', 'Add connector') },
    { id: 'dataunfold', title: t('dag.section_dataunfold', 'Your workspace'), subtitle: t('dag.section_dataunfold_hint', 'Everything together in your project'),
      icon: WorkspacesOutlinedIcon, color: '#5757b0', background: 'rgba(87, 87, 176, 0.055)' },
    { id: 'actions', title: t('dag.section_actions', 'Choose what happens next'), subtitle: t('dag.section_actions_hint', 'Create reports, update dashboards, and more'),
      icon: AccountTreeOutlinedIcon, color: '#387660', background: 'rgba(56, 118, 96, 0.035)',
      onAdd: onAddFlow, addLabel: t('dag.add_flow', 'Add flow') },
  ];

  return <Box data-testid="dag-sections" sx={{
    position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden',
  }}>
    {sections.map((section, index) => {
      const sectionWidth = boundaries[index + 1] - boundaries[index];
      if (sectionWidth <= 0) return null;
      return <Box key={section.id} role="region" aria-label={section.title}
        data-testid={`dag-section-${section.id}`} sx={{
          position: 'absolute', top: 0, bottom: 0, left: boundaries[index], width: sectionWidth,
          boxSizing: 'border-box', overflow: 'hidden',
        }}>
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, backgroundColor: section.background,
          borderRight: index < 2 && boundaries[index + 1] < width
            ? '1px dashed rgba(87, 87, 176, 0.22)' : 'none',
        }} />
        {sectionWidth >= (boundaries[index] === 0 ? 128 : 48) && <SectionHeading section={section} width={sectionWidth} />}
      </Box>;
    })}
  </Box>;
}
